import React from 'react';
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { useToast } from '../../context/ToastContext';

/**
 * PayPalCheckout Component
 * 
 * @param {string} type - 'JOB' or 'SUBSCRIPTION'
 * @param {string} amount - The amount to charge (for 'JOB')
 * @param {string} planId - The PayPal Plan ID (for 'SUBSCRIPTION')
 * @param {number} itemId - The ID of the job (for 'JOB')
 * @param {string} level - The target level ('SILVER', 'GOLD')
 * @param {function} onSuccess - Callback after successful capture
 */
const PayPalCheckout = ({ type, amount, planId, itemId, level, onSuccess }) => {
    const [{ isPending }] = usePayPalScriptReducer();
    const { showToast } = useToast();

    const handleApproveOrder = async (data, actions) => {
        try {
            const response = await fetch('/api/billing/paypal/capture-job/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}` // Simplified
                },
                body: JSON.stringify({
                    orderID: data.orderID,
                    jobID: itemId
                })
            });

            if (response.ok) {
                showToast("Zahlung erfolgreich! Job ist jetzt hervorgehoben.", "success");
                if (onSuccess) onSuccess();
            } else {
                const err = await response.json();
                showToast(err.error || "Fehler beim Bestätigen der Zahlung.", "error");
            }
        } catch (error) {
            console.error("PayPal Capture Error:", error);
            showToast("Verbindungsfehler beim Erfassen der Zahlung.", "error");
        }
    };

    const handleApproveSubscription = async (data, actions) => {
        try {
            const response = await fetch('/api/billing/paypal/verify-subscription/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify({
                    subscriptionID: data.subscriptionID,
                    level: level
                })
            });

            if (response.ok) {
                showToast(`Willkommen als ${level}-Partner!`, "success");
                if (onSuccess) onSuccess();
            } else {
                const err = await response.json();
                showToast(err.error || "Fehler beim Aktivieren des Abos.", "error");
            }
        } catch (error) {
            console.error("PayPal Subscription Error:", error);
            showToast("Verbindungsfehler beim Aktivieren des Abos.", "error");
        }
    };

    if (isPending) return <div className="animate-pulse bg-gray-100 dark:bg-white/5 h-12 rounded-xl" />;

    return (
        <div className="w-full">
            {type === 'JOB' ? (
                <PayPalButtons
                    style={{ layout: "vertical", shape: "rect", label: "pay" }}
                    createOrder={(data, actions) => {
                        return actions.order.create({
                            purchase_units: [
                                {
                                    description: `Featured Job Upgrade: ${itemId}`,
                                    amount: {
                                        currency_code: "EUR",
                                        value: amount
                                    }
                                }
                            ]
                        });
                    }}
                    onApprove={handleApproveOrder}
                    onError={(err) => {
                        console.error("PayPal Error:", err);
                        showToast("PayPal-Zahlung fehlgeschlagen.", "error");
                    }}
                />
            ) : (
                <PayPalButtons
                    style={{ layout: "vertical", shape: "rect", label: "subscribe" }}
                    createSubscription={(data, actions) => {
                        return actions.subscription.create({
                            plan_id: planId
                        });
                    }}
                    onApprove={handleApproveSubscription}
                    onError={(err) => {
                        console.error("PayPal Subscription Error:", err);
                        showToast("PayPal-Abo konnte nicht gestartet werden.", "error");
                    }}
                />
            )}
        </div>
    );
};

export default PayPalCheckout;
